<template>
  <div>
    <v-alert type="warning" variant="tonal" density="compact" class="mb-3" icon="mdi-shield-lock-outline">
      <span class="text-body-2">
        {{ $t('qaMaskAlwaysOn') }}
      </span>
    </v-alert>

    <v-row dense>
      <v-col cols="12" md="7">
        <v-card class="qa-card-sm mb-3" variant="outlined">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaMaskRulesTitle') }}</span>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">{{ $t('qaMaskBeforeLlm') }}</span>
          </v-card-title>
          <v-divider />
          <v-table density="comfortable" class="qa-mask-table">
            <thead>
              <tr>
                <th>{{ $t('qaMaskColType') }}</th>
                <th>{{ $t('qaMaskColShape') }}</th>
                <th style="width: 110px">{{ $t('qaMaskColStatus') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- แถวมาจาก catalog (ชื่อ/รูปแบบ/บังคับหรือไม่ อยู่ในโค้ด backend) —
                   ค่าตั้งเก็บแค่ เปิด/ปิด ต่อ ruleId · แก้ชื่อกฎจากหน้านี้ไม่ได้โดยตั้งใจ -->
              <tr v-for="row in ruleRows" :key="row.def.ruleId">
                <td>
                  <div class="text-body-2 font-weight-medium">{{ row.def.label }}</div>
                  <div v-if="row.def.note" class="text-caption text-medium-emphasis">{{ row.def.note }}</div>
                </td>
                <td><code class="qa-mask-code">{{ row.def.maskedFormat }}</code></td>
                <td>
                  <div v-if="row.def.isMandatory" class="d-flex align-center ga-1">
                    <v-icon size="16" color="error">mdi-lock</v-icon>
                    <v-chip color="error" size="small" variant="tonal" label>{{ $t('qaMaskMandatory') }}</v-chip>
                  </div>
                  <v-switch v-else-if="row.rule" v-model="row.rule.isEnabled" color="primary" density="compact" hide-details inset />
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card>

        <v-card class="qa-card-sm" variant="outlined">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaMaskRegexTitle') }}</span>
            <v-spacer />
            <span class="text-caption text-medium-emphasis qa-num">{{ value.customPatterns.length }} / {{ maxCustomPatterns }}</span>
          </v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="text-caption text-medium-emphasis mb-3">
              {{ $t('qaMaskRegexNote') }}
            </div>
            <div v-for="(pattern, i) in value.customPatterns" :key="i" class="d-flex align-center ga-2 mb-2">
              <v-text-field
                v-model="value.customPatterns[i]"
                density="compact"
                variant="outlined"
                hide-details
                spellcheck="false"
                class="qa-pattern-input"
                :placeholder="'\\bAPP-\\d{6}\\b'"
              />
              <v-btn icon size="small" variant="text" color="error" @click="value.customPatterns.splice(i, 1)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="value.customPatterns.length >= maxCustomPatterns"
              @click="value.customPatterns.push('')"
            >
              {{ $t('qaMaskAddPattern') }}
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="qa-card-sm" variant="outlined">
          <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaMaskTryTitle') }}</span></v-card-title>
          <v-divider />
          <div class="pa-4">
            <!-- ทดลองกับกฎชุดที่**กำลังแก้บนหน้าจอ** (ยังไม่ต้องบันทึก) — server เป็นคน mask
                 ด้วยโค้ดเดียวกับ worker จริง ไม่ใช่ regex จำลองฝั่ง JS ที่อาจให้ผลต่าง -->
            <v-textarea
              v-model="previewText"
              variant="outlined"
              rows="3"
              hide-details
              auto-grow
              spellcheck="false"
              :placeholder="$t('qaMaskTryPlaceholder')"
            />
            <v-btn size="small" color="primary" variant="tonal" class="mt-2" prepend-icon="mdi-eye-outline" :loading="previewing" :disabled="!previewText.trim()" @click="runPreview">
              {{ $t('qaMaskTryRun') }}
            </v-btn>

            <template v-if="preview">
              <div class="text-caption text-medium-emphasis mb-1 mt-4">{{ $t('qaMaskOriginal') }}</div>
              <v-card variant="flat" class="pa-3 text-body-2 mb-3 qa-sample-raw">{{ preview.original }}</v-card>
              <div class="text-caption text-medium-emphasis mb-1">{{ $t('qaMaskSentToLlm') }}</div>
              <v-card variant="flat" class="pa-3 text-body-2 qa-sample-masked">{{ preview.masked }}</v-card>
            </template>
            <div class="text-caption text-medium-emphasis mt-3">
              {{ $t('qaMaskTryNote') }}
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaMaskSave') }}</v-btn>
        <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
          {{ $t('qaSetLastEditedBy') }} {{ updatedBy || $t('qaSetUnknown') }} · {{ updatedAtText }}
        </span>
        <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSetNeverSaved') }}</span>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/**
 * Q9 — แท็บการปกปิดข้อมูล (ของจริง · cc.QaSettings['masking'])
 *
 * รูปข้อมูลจาก GET settings/masking:
 *   { value: { rules: [{ruleId, isEnabled}], customPatterns: [] }, catalog: [...], updatedAt, updatedBy }
 *   - catalog คือสารบัญกฎจากโค้ด backend (ชื่อ/รูปแบบ/บังคับหรือไม่) — หน้าจอไม่มีสำเนาของตัวเอง
 *     ไม่งั้นเพิ่มกฎฝั่ง backend แล้วหน้านี้จะไม่รู้จัก
 *   - value คือสิ่งเดียวที่แก้ได้: เปิด/ปิดกฎไม่บังคับ + custom regex
 *
 * ⚠️ save อาจได้ 400 เมื่อ custom regex ไม่ผ่าน validation (compile ไม่ได้ / เสี่ยงระเบิด) —
 *    ข้อความจาก server ระบุรูปแบบที่พังพร้อมเหตุผล ต้องส่งต่อให้ผู้ใช้เห็นตรง ๆ
 */
import qaConsole from '@/services/qaConsole';

// ตรงกับ QaMaskingSettings.MaxCustomPatterns ฝั่ง server — เกินนี้ server ตัดทิ้งเงียบ ๆ
// จึงห้ามให้กรอกเกินตั้งแต่หน้าจอ
const MAX_CUSTOM_PATTERNS = 10;

export default {
  name: 'QaMaskingSettings',
  emits: ['saved'],
  data() {
    return {
      value: { rules: [], customPatterns: [] },
      catalog: [],
      updatedAt: null,
      updatedBy: null,
      saving: false,
      previewText: '',
      preview: null,
      previewing: false,
      maxCustomPatterns: MAX_CUSTOM_PATTERNS,
    };
  },
  computed: {
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
    /**
     * แถวบนตาราง = catalog (ข้อมูลแสดงผล) join ค่าตั้ง (สถานะเปิด/ปิด) ด้วย ruleId
     * row.rule ชี้ไปที่ object จริงใน value.rules — สวิตช์แก้ค่าตั้งตรง ๆ ไม่ต้อง sync กลับ
     */
    ruleRows() {
      return this.catalog.map((def) => ({
        def,
        rule: this.value.rules.find((r) => r.ruleId === def.ruleId) || null,
      }));
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    applyResult(res) {
      if (!res) return;
      const value = res.value || {};
      this.value = {
        rules: Array.isArray(value.rules) ? value.rules : [],
        customPatterns: Array.isArray(value.customPatterns) ? value.customPatterns : [],
      };
      this.catalog = Array.isArray(res.catalog) ? res.catalog : [];
      // server (Normalized) การันตีว่ามีแถวครบทุกกฎในสารบัญ — เผื่อไว้อยู่ดีสำหรับกฎใหม่
      // ที่เพิ่มฝั่ง backend หลังจากแถวค่าตั้งถูกบันทึกไว้ก่อน
      for (const def of this.catalog) {
        if (!this.value.rules.some((r) => r.ruleId === def.ruleId)) {
          this.value.rules.push({ ruleId: def.ruleId, isEnabled: def.isMandatory || def.defaultEnabled });
        }
      }
      this.updatedAt = res.updatedAt || null;
      this.updatedBy = res.updatedBy || null;
    },
    async load() {
      try {
        this.applyResult(await qaConsole.getMaskingRules());
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaMaskLoadFailed', { msg: err.message }) });
      }
    },
    async runPreview() {
      this.previewing = true;
      try {
        // ส่งค่าตั้งที่กำลังแก้ไปด้วย — ผลที่เห็นคือผลของสิ่งที่กำลังจะบันทึก ไม่ใช่ของเก่าในฐาน
        this.preview = await qaConsole.previewMasking({ text: this.previewText, settings: this.value });
      } catch (err) {
        this.preview = null;
        this.$emit('saved', { ok: false, message: this.$t('qaMaskTryFailed', { msg: err.message }) });
      } finally {
        this.previewing = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        // ตัดช่องที่เพิ่มไว้แต่ยังว่างออกก่อนส่ง — ช่องว่างไม่ใช่ regex ที่ตั้งใจ
        this.value.customPatterns = this.value.customPatterns.map((p) => (p || '').trim()).filter((p) => p.length > 0);
        const res = await qaConsole.saveMaskingRules(this.value);
        this.applyResult(res);
        this.$emit('saved', { ok: true, message: this.$t('qaMaskSaved') });
      } catch (err) {
        // 400 = custom regex ไม่ผ่าน — err.message จาก server บอกว่ารูปแบบไหนพังเพราะอะไร
        this.$emit('saved', { ok: false, message: err.message });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ใช้ class แทน variant="tonal" เพื่อไม่ให้ Vuetify ย้อมสีตัวอักษรจนอ่านยาก */
.qa-sample-raw {
  background: #fafbfc;
  border: 1px solid #eef0f2;
}
.qa-sample-masked {
  background: #eef7ef;
  border: 1px solid #cfe6d1;
}
.qa-mask-table :deep(th) {
  background: #f5f5f5;
  font-size: 11px;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
}
.qa-mask-code {
  background: #f0f2f5;
  border-radius: 5px;
  padding: 2px 7px;
  font-size: 12px;
  color: #334;
}
/* regex อ่านใน monospace เท่านั้น — จุดกับจุลภาคใน font ปกติแยกไม่ออก */
.qa-pattern-input :deep(input) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}
</style>
